import React, { useState } from 'react';
import { navigate } from 'gatsby';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCart } from '@printdeal/checkout-frontend';
import { Button, Heading, LinkAsButton } from '@printdeal/ui-library';
import {
  AddressDeleteModal, AddressFormSubmitButton, UpdateAddressView, useGetAddressesQuery,
} from '@printdeal/address-frontend';
import Breadcrumb from '../../../../Breadcrumb';
import Head from '../../../../PrivateHead';

interface Props {
  addressId: string;
}

const UpdateAddressPage = ({ addressId }: Props) => {
  const { invalidateCart, cartId } = useCart();
  const intl = useIntl();

  const getAddressesQuery = useGetAddressesQuery();
  const isDefaultShippingAddress = getAddressesQuery.data?.addresses.find(
    (address) => address.address.id === addressId && address.isDefaultShippingAddress,
  );
  const isDefaultBillingAddress = getAddressesQuery.data?.addresses.find(
    (address) => address.address.id === addressId && address.isDefaultBillingAddress,
  );

  const [deleteAddressModalIsOpen, setDeleteAddressModalIsOpen] = useState<string | undefined>();

  return (
    <div className="pd-flex pd-flex-col pd-gap-3">
      <Head title={intl.formatMessage({ id: 'account.addressCreate.title', defaultMessage: 'Create address' })} />
      <Breadcrumb>
        <Breadcrumb.Item to="../../..">
          <FormattedMessage id="account.index.title" defaultMessage="My account" />
        </Breadcrumb.Item>
        <Breadcrumb.Item to="../..">
          <FormattedMessage id="account.addressList.title" defaultMessage="My addressbook" />
        </Breadcrumb.Item>
        <Breadcrumb.Item isCurrentPage>
          <FormattedMessage id="account.editAddress.title" defaultMessage="Edit address" />
        </Breadcrumb.Item>
      </Breadcrumb>

      <div className="pd-flex pd-items-center pd-justify-between">
        <Heading as="h2">
          <FormattedMessage id="account.editAddress.title" defaultMessage="Edit address" />
        </Heading>

        {(!isDefaultShippingAddress && !isDefaultBillingAddress) && (
          <>
            <Button variant="secondary" onClick={() => setDeleteAddressModalIsOpen(addressId)}>
              <FormattedMessage id="address.delete" defaultMessage="Delete address" />
            </Button>

            <AddressDeleteModal
              addressId={addressId}
              isModalOpen={deleteAddressModalIsOpen === addressId}
              setIsModalOpen={setDeleteAddressModalIsOpen}
              onSuccess={() => {
                navigate('../..');
              }}
            />
          </>
        )}
      </div>

      <UpdateAddressView
        mutatingAddressId={addressId}
        onSubmitSuccess={() => {
          if (cartId) {
            // The update address form might have updated the cart, so we need to refresh it.
            invalidateCart();
          }

          navigate('../..', {
            state: {
              showSuccessAlertForUpdatedItem: addressId,
            },
          });
        }}
      />

      <div className="pd-flex pd-items-center pd-justify-between">
        <LinkAsButton
          className="pd-text-sm"
          iconBefore="chevron-left"
          variant="secondary"
          onClick={() => navigate('../..')}
        >
          <FormattedMessage id="checkout.backToAddressList" defaultMessage="Back to address list" />
        </LinkAsButton>

        <AddressFormSubmitButton />
      </div>
    </div>
  );
};

export default UpdateAddressPage;
